export const WAITING_ROOM_VERSION = 1;

export const WAITING_ROOM_EVENTS = {
    request: 'waiting-room/request',
    received: 'waiting-room/received',
    admit: 'waiting-room/admit',
    deny: 'waiting-room/deny',
    resolved: 'waiting-room/resolved',
};

const CONNECTED_CATEGORIES = new Set(['PNConnectedCategory', 'PNReconnectedCategory']);
const RESOLVED_REQUEST_TTL_MS = 60000;

export const getSessionPresenceRole = (userInvite) =>
    userInvite ? 'participant' : 'moderator';

export const createRequestId = (participantId) => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }

    return `${participantId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createWaitingRoomRequest = ({ participantId, displayName, clientType }) => {
    const requestedAt = Date.now();

    return {
        type: WAITING_ROOM_EVENTS.request,
        version: WAITING_ROOM_VERSION,
        requestId: createRequestId(participantId),
        participantId,
        displayName,
        clientType,
        requestedAt,
        sentAt: requestedAt,
    };
};

export const isWaitingRoomEvent = (message, type) =>
    message &&
    message.version === WAITING_ROOM_VERSION &&
    message.type === type &&
    typeof message.requestId === 'string' &&
    typeof message.participantId === 'string';

export const normalizeWaitingRoomRequest = (message, publisher) => {
    if (!isWaitingRoomEvent(message, WAITING_ROOM_EVENTS.request)) return null;

    const participantId = message.participantId || publisher;
    if (!participantId) return null;

    return {
        requestId: message.requestId,
        participantId,
        displayName: String(message.displayName || 'Guest'),
        clientType: message.clientType || 'now-translate',
        requestedAt: Number(message.requestedAt || message.sentAt) || Date.now(),
        receivedAt: Date.now(),
    };
};

export const requestFromPresence = (occupant) => {
    const state = occupant?.state;
    const participantId = occupant?.uuid;
    if (state?.role !== 'waiting' || !state.requestId || !participantId) return null;

    return {
        requestId: state.requestId,
        participantId,
        displayName: String(state.displayName || 'Guest'),
        clientType: state.clientType || 'now-translate',
        requestedAt: Number(state.requestedAt) || Date.now(),
        receivedAt: Date.now(),
    };
};

const sortWaitingRoomQueue = (queue) =>
    [...queue].sort(
        (first, second) =>
            first.requestedAt - second.requestedAt ||
            first.receivedAt - second.receivedAt ||
            first.requestId.localeCompare(second.requestId),
    );

export const upsertWaitingRoomRequest = (queue, request) => {
    if (!request) return queue;

    const existingIndex = queue.findIndex(
        (item) =>
            item.requestId === request.requestId || item.participantId === request.participantId,
    );

    if (existingIndex < 0) return sortWaitingRoomQueue([...queue, request]);

    const existing = queue[existingIndex];
    const updated = {
        ...existing,
        ...request,
        receivedAt: existing.receivedAt,
    };

    if (
        existing.requestId === updated.requestId &&
        existing.displayName === updated.displayName &&
        existing.clientType === updated.clientType &&
        existing.requestedAt === updated.requestedAt
    ) {
        return queue;
    }

    const nextQueue = [...queue];
    nextQueue[existingIndex] = updated;
    return sortWaitingRoomQueue(nextQueue);
};

export const removeWaitingRoomRequest = (queue, { requestId, participantId }) =>
    queue.filter(
        (request) =>
            request.requestId !== requestId && request.participantId !== participantId,
    );

const publishEvent = (client, room, message) =>
    client.publish({
        channel: room,
        message,
        storeInHistory: false,
    });

export const createGuestWaitingRoomController = ({
    client,
    room,
    participantId,
    clientType,
    onModeratorPresence,
    onAdmit,
    onDeny,
    retryMs = 5000,
}) => {
    let currentRequest = null;
    let acknowledged = false;
    let retryTimer = null;
    let resolving = false;

    const clearRetry = () => {
        if (retryTimer) {
            clearInterval(retryTimer);
            retryTimer = null;
        }
    };

    const refreshModeratorPresence = async () => {
        try {
            const response = await client.hereNow({
                channels: [room],
                includeState: true,
            });
            const occupants = response.channels?.[room]?.occupants || [];
            onModeratorPresence(
                occupants.some((occupant) => occupant.state?.role === 'moderator'),
            );
        } catch (error) {
            console.error('Unable to refresh moderator presence', error);
        }
    };

    const setPresence = (state) =>
        client.setState({
            channels: [room],
            state,
        });

    const sendRequest = async () => {
        if (!currentRequest || resolving) return;

        currentRequest = { ...currentRequest, sentAt: Date.now() };
        await Promise.allSettled([
            setPresence({
                role: 'waiting',
                requestId: currentRequest.requestId,
                displayName: currentRequest.displayName,
                clientType: currentRequest.clientType,
                requestedAt: currentRequest.requestedAt,
            }),
            publishEvent(client, room, currentRequest),
        ]);
    };

    const startRetry = () => {
        clearRetry();
        retryTimer = setInterval(() => {
            if (!acknowledged) sendRequest();
        }, retryMs);
    };

    const resolveRequest = async (resolution) => {
        if (!currentRequest || resolving) return;

        resolving = true;
        const request = currentRequest;
        currentRequest = null;
        acknowledged = false;
        clearRetry();

        await Promise.allSettled([
            setPresence({ role: resolution === 'admit' ? 'participant' : 'idle' }),
            publishEvent(client, room, {
                type: WAITING_ROOM_EVENTS.resolved,
                version: WAITING_ROOM_VERSION,
                requestId: request.requestId,
                participantId,
                resolution,
                sentAt: Date.now(),
            }),
        ]);

        if (resolution === 'admit') {
            await onAdmit();
        } else {
            onDeny();
        }
    };

    const messageMatchesCurrentRequest = (message, type) =>
        currentRequest &&
        isWaitingRoomEvent(message, type) &&
        message.requestId === currentRequest.requestId &&
        message.participantId === participantId;

    const handleResolution = (resolution) =>
        resolveRequest(resolution).catch((error) =>
            console.error(`Unable to process waiting-room ${resolution}`, error),
        );

    const listener = {
        message: ({ channel, message }) => {
            if (channel && channel !== room) return;

            if (messageMatchesCurrentRequest(message, WAITING_ROOM_EVENTS.received)) {
                acknowledged = true;
                clearRetry();
            } else if (messageMatchesCurrentRequest(message, WAITING_ROOM_EVENTS.admit)) {
                return handleResolution('admit');
            } else if (messageMatchesCurrentRequest(message, WAITING_ROOM_EVENTS.deny)) {
                return handleResolution('deny');
            }
        },
        presence: (event) => {
            if (event.channel && event.channel !== room) return;

            if (
                (event.action === 'join' || event.action === 'state-change') &&
                event.state?.role === 'moderator'
            ) {
                onModeratorPresence(true);
            } else if (
                event.action === 'leave' ||
                event.action === 'timeout' ||
                event.action === 'state-change'
            ) {
                refreshModeratorPresence();
            }
        },
        status: ({ category }) => {
            if (!CONNECTED_CATEGORIES.has(category)) return;

            refreshModeratorPresence();
            if (currentRequest) {
                acknowledged = false;
                sendRequest();
                startRetry();
            }
        },
    };

    return {
        connect() {
            client.addListener(listener);
            client.subscribe({ channels: [room], withPresence: true });
            refreshModeratorPresence();
        },
        disconnect() {
            clearRetry();
            client.removeListener(listener);
        },
        async request(displayName) {
            currentRequest = createWaitingRoomRequest({
                participantId,
                displayName,
                clientType,
            });
            acknowledged = false;
            resolving = false;
            await sendRequest();
            startRetry();
            return currentRequest;
        },
        cancel() {
            currentRequest = null;
            acknowledged = false;
            resolving = false;
            clearRetry();
        },
    };
};

export const createModeratorWaitingRoomController = ({ client, room, onQueueChange }) => {
    let queue = [];
    const resolvedRequests = new Map();

    const emitQueue = (nextQueue) => {
        if (nextQueue === queue) return;
        queue = nextQueue;
        onQueueChange([...queue]);
    };

    const pruneResolvedRequests = () => {
        const cutoff = Date.now() - RESOLVED_REQUEST_TTL_MS;
        resolvedRequests.forEach(({ resolvedAt }, key) => {
            if (resolvedAt < cutoff) resolvedRequests.delete(key);
        });
    };

    const getRecentResolution = (request) => {
        pruneResolvedRequests();
        return resolvedRequests.get(request.requestId);
    };

    const rememberResolved = (request, resolution) => {
        resolvedRequests.set(request.requestId, {
            participantId: request.participantId,
            resolution,
            resolvedAt: Date.now(),
        });
    };

    const sendReceived = (request) =>
        publishEvent(client, room, {
            type: WAITING_ROOM_EVENTS.received,
            version: WAITING_ROOM_VERSION,
            requestId: request.requestId,
            participantId: request.participantId,
            sentAt: Date.now(),
        }).catch((error) => console.error('Unable to acknowledge waiting-room request', error));

    const sendResolution = (request, resolution) =>
        publishEvent(client, room, {
            type: resolution === 'admit' ? WAITING_ROOM_EVENTS.admit : WAITING_ROOM_EVENTS.deny,
            version: WAITING_ROOM_VERSION,
            requestId: request.requestId,
            participantId: request.participantId,
            sentAt: Date.now(),
        });

    const addRequest = (request) => {
        if (!request) return;

        const previousResolution = getRecentResolution(request);
        if (previousResolution) {
            sendResolution(request, previousResolution.resolution).catch((error) =>
                console.error('Unable to replay waiting-room resolution', error),
            );
            return;
        }

        emitQueue(upsertWaitingRoomRequest(queue, request));
        sendReceived(request);
    };

    const removeRequest = (request) => {
        emitQueue(removeWaitingRoomRequest(queue, request));
    };

    const reconcilePresence = async () => {
        try {
            const response = await client.hereNow({
                channels: [room],
                includeState: true,
            });
            const occupants = response.channels?.[room]?.occupants || [];
            const presenceRequests = occupants
                .map(requestFromPresence)
                .filter(Boolean)
                .sort((first, second) => first.requestedAt - second.requestedAt);
            const waitingParticipantIds = new Set(
                presenceRequests.map((request) => request.participantId),
            );
            let reconciledQueue = queue.filter((request) =>
                waitingParticipantIds.has(request.participantId),
            );

            presenceRequests.forEach((request) => {
                if (!getRecentResolution(request)) {
                    reconciledQueue = upsertWaitingRoomRequest(reconciledQueue, request);
                }
            });
            emitQueue(reconciledQueue);
        } catch (error) {
            console.error('Unable to reconcile waiting-room presence', error);
        }
    };

    const setModeratorPresence = () =>
        client
            .setState({
                channels: [room],
                state: { role: 'moderator' },
            })
            .catch((error) => console.error('Unable to set moderator presence', error));

    const listener = {
        message: ({ channel, message, publisher }) => {
            if (channel && channel !== room) return;

            const request = normalizeWaitingRoomRequest(message, publisher);
            if (request) {
                addRequest(request);
                return;
            }

            if (isWaitingRoomEvent(message, WAITING_ROOM_EVENTS.resolved)) {
                rememberResolved(message, message.resolution);
                removeRequest(message);
            }
        },
        presence: (event) => {
            if (event.channel && event.channel !== room) return;

            if (
                (event.action === 'join' || event.action === 'state-change') &&
                event.state?.role === 'waiting'
            ) {
                addRequest(
                    requestFromPresence({
                        uuid: event.uuid,
                        state: event.state,
                    }),
                );
            } else if (
                event.action === 'leave' ||
                event.action === 'timeout' ||
                (event.action === 'state-change' && event.state?.role !== 'waiting')
            ) {
                removeRequest({ participantId: event.uuid });
            }
        },
        status: ({ category }) => {
            if (!CONNECTED_CATEGORIES.has(category)) return;
            setModeratorPresence();
            reconcilePresence();
        },
    };

    const resolve = async (request, resolution) => {
        await sendResolution(request, resolution);
        rememberResolved(request, resolution);
        removeRequest(request);
    };

    return {
        connect() {
            client.addListener(listener);
            client.subscribe({ channels: [room], withPresence: true });
            setModeratorPresence();
            reconcilePresence();
        },
        disconnect() {
            client.removeListener(listener);
        },
        admit(request) {
            return resolve(request, 'admit');
        },
        deny(request) {
            return resolve(request, 'deny');
        },
        reconcile: reconcilePresence,
        getQueue() {
            return [...queue];
        },
    };
};
